import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  TextField as MuiTextField,
  Grid,
  Select,
  MenuItem,
  Typography,
  Stack,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  FormControl,
  InputLabel,
} from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { axiosRequest } from "../axiosConfig";
import { debounce } from 'lodash';

let currentdtemp = new Date();
const year = currentdtemp.getFullYear();
const month = String(currentdtemp.getMonth() + 1).padStart(2, "0");
const day = String(currentdtemp.getDate()).padStart(2, "0");

const bgColor = "rgb(210 210 210 / 10%)";
const bgColorDisabled = "rgb(210 210 210 / 12%)";
const disabledOCRTextColor = "#515151";
const disabledDefaultTextColor = undefined;

const debouncedLookup = debounce((npi, faxId, childFaxId, handler) => {
  handler(npi, faxId, childFaxId);
}, 500);

const CustomTextField = ({
  pk,
  keyName,
  editable,
  patientErrorMessage,
  editeValue,
  type = "text",
  setHoveredFieldKey,
  highlightData,
  pinnedHighlights,
  togglePinnedHighlight,
  reProcessFax,
  faxId,
  childFaxId,
  onFieldFocus,
  selectedFieldKey,
  ...fieldProps
}) => {

  // Add at the top of your CustomTextField component
  const [npiUpdateSuccess, setNpiUpdateSuccess] = useState(false);
  const [isReprocessing, setIsReprocessing] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const previousNpiValueRef = useRef(null);

  useEffect(() => {
    let timer;
    if (npiUpdateSuccess) {
      timer = setTimeout(() => {
        setNpiUpdateSuccess(false);
      }, 3000); // Message will disappear after 3 seconds
    }
    return () => clearTimeout(timer);
  }, [npiUpdateSuccess]);

  const isInitialMount = useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (isProviderNPI && pk?.labelValue?.length === 10 &&
        /^\d{10}$/.test(pk.labelValue) && editable) {
      if (previousNpiValueRef.current !== pk.labelValue) {
        debouncedLookup(pk.labelValue, faxId, childFaxId, handleNpiLookup);
        previousNpiValueRef.current = pk.labelValue;
      }
    }

    return () => {
      debouncedLookup.cancel();
    };
  }, [pk?.labelValue, faxId, childFaxId]);

  const formatDateValue = (value) => {
    if (type === "date") {
      if (editable) {
        // For editable date inputs, ensure YYYY-MM-DD format
        if (!value) return "";
        // If it's already in correct format, return as is
        if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
        // Convert from MM/DD/YYYY to YYYY-MM-DD
        const parts = value.split("/");
        if (parts.length === 3) {
          return `${parts[2]}-${parts[0].padStart(2, "0")}-${parts[1].padStart(
            2,
            "0"
          )}`;
        }
        return value;
      } else {
        // For non-editable, keep MM/DD/YYYY format
        return value || "";
      }
    }
    return value || "";
  };

  const getHighlightKey = (bindValue, keyName) => {
    const mappings = {
      FirstName: "Patient.Patient Name.Patient First Name",
      LastName: "Patient.Patient Name.Patient Last Name",
      Gender: "Patient.Patient Gender",
      DateOfBirth: "Patient.Patient DOB",
      Contact: "Patient.Patient Phone.Patient Phone Cell",
      ProviderFirstName: "Provider.Provider First Name",
      ProviderLastName: "Provider.Provider Last Name",
      ProviderPhone: "Provider.Provider Phone",
      ProviderFax: "Provider.Provider Fax",
      ProviderNPI: "Provider.Provider NPI",
      DateWritten: "Date Written",
      LengthOfNeed: "Length of Need",
      DiagnosisCode: "Diagnosis Code",
    };
    return mappings[pk?.labelBindValue];
  };

  const verifyProviderNPI = async (npi, documentId, childDocumentId = null) => {
    try {
      const response = await axiosRequest.post('/verify-provider-npi', {
        npi: npi,
        documentId: documentId,
        childDocumentId: childDocumentId
      });

      console.log("verify npi response: ", response.data);

      return response.data;
    } catch (error) {
      console.error('Error verifying NPI:', error);
      throw error;
    }
  };

  const handleNpiLookup = async (npi) => {
    // Don't proceed if already reprocessing
    if (isReprocessing) return;

    try {
      setIsReprocessing(true);
      const providerVerification = await verifyProviderNPI(npi, faxId, childFaxId);

      if(providerVerification['providerType']) {
        await reProcessFax();
        setNpiUpdateSuccess(true);
      }
    } catch (error) {
      console.error("Error during NPI lookup:", error);
    } finally {
      setIsReprocessing(false);
    }
  };

  const highlightKey = getHighlightKey(pk?.labelBindValue, keyName);
  const hasCoordinates =
    highlightKey && highlightData?.coordinates?.[highlightKey];

  const handleFieldClick = () => {
    if (highlightKey && hasCoordinates) {
      setHoveredFieldKey(highlightKey);
      if (onFieldFocus) {
        onFieldFocus(highlightKey);
      }
    } else {
      if (onFieldFocus) {
        onFieldFocus(null);
      }
    }
  };

  // Check if this is the Provider NPI field
  const isProviderNPI = pk?.labelBindValue === "ProviderNPI";

 const fieldBackgroundColor =
   hasCoordinates && selectedFieldKey === highlightKey
     ? "#FFFFBB"
     : editable
     ? bgColor
     : bgColorDisabled;

  const fieldStyles = {
    "& .MuiFilledInput-root": {
      bgcolor: fieldBackgroundColor,
      "&.Mui-disabled": {
        bgcolor: fieldBackgroundColor,
      },
      "&:before": {
        borderBottomColor:
          hasCoordinates && selectedFieldKey === highlightKey
            ? "#FACD56"
            : hasCoordinates
            ? "#00AB4E"
            : "#202020",
      },
      "&.Mui-disabled:before": {
        borderBottomStyle: "dashed",
      },
      "&:hover:not(.Mui-disabled):before": {
        borderBottomColor:
          hasCoordinates && selectedFieldKey === highlightKey
            ? "#FACD56"
            : hasCoordinates
            ? "#00AB4E"
            : "#202020",
      },
      "&:after": {
        borderBottomColor: hasCoordinates ? "#FACD56" : "#202020",
      },
      "& .MuiInputBase-input.Mui-disabled": {
        color: hasCoordinates ? disabledOCRTextColor : disabledDefaultTextColor ,
        "-webkit-text-fill-color": hasCoordinates ? disabledOCRTextColor : disabledDefaultTextColor ,
      },

      "&.Mui-disabled .MuiInputBase-input": {
        color: hasCoordinates ? disabledOCRTextColor : disabledDefaultTextColor ,
        "-webkit-text-fill-color": hasCoordinates ? disabledOCRTextColor : disabledDefaultTextColor ,
      },
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "rgba(0, 0, 0, 0.6)",
    },
    "& .MuiInputBase-root": {
      pointerEvents: hasCoordinates && !editable ? "none" : "auto",
    },
  };

  return (
    <div
      onClick={handleFieldClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        cursor: hasCoordinates ? "pointer" : "default",
        position: "relative",
      }}
    >
      <div style={{ position: "relative", width: "100%" }}>
        <MuiTextField
          sx={fieldStyles}
          size="small"
          label={pk?.labelName}
          variant="filled"
          fullWidth
          disabled={!editable}
          value={formatDateValue(pk?.labelValue)}
          error={Boolean(
            patientErrorMessage[keyName]?.[pk?.labelBindValue]?.length
          )}
          helperText={patientErrorMessage[keyName]?.[pk?.labelBindValue] || ""}
          required={
            pk?.labelBindValue !== "ProviderFax" && Boolean(pk?.labelStartColor)
          }
          onChange={editable ? (e) => editeValue(e, keyName) : undefined}
          type={type}
          name={pk?.labelBindValue}
          {...fieldProps}
        />
      </div>

      {/* Render the button only for Provider NPI field */}
      {isProviderNPI && (
        <>
          <Box sx={{ position: "relative" }}>
            <IconButton
              onClick={() => handleNpiLookup(pk.labelValue)}
              size="small"
              sx={{ ml: 1 }}
              disabled={isReprocessing || !editable}
              onMouseEnter={() => setTooltipOpen(true)}
              onMouseLeave={() => setTooltipOpen(false)}
            >
              {isReprocessing ? (
                <CircularProgress size={20} />
              ) : (
                <AutorenewIcon />
              )}
            </IconButton>

            {/* Custom tooltip */}
            {tooltipOpen && !isReprocessing && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: "100%",
                  left: "50%",
                  transform: "translateX(-50%)",
                  backgroundColor: "rgba(25, 25, 25,0.4)",
                  color: "white",
                  padding: "5px 10px",
                  borderRadius: "4px",
                  fontSize: "0.8rem",
                  marginBottom: "5px",
                  whiteSpace: "nowrap",
                  zIndex: 1500,
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    top: "100%",
                    left: "50%",
                    marginLeft: "-5px",
                    borderWidth: "5px",
                    borderStyle: "solid",
                    borderColor:
                      "rgba(0,0,0,0.8) transparent transparent transparent",
                  },
                }}
              >
                Update Provider Information
              </Box>
            )}
          </Box>
          {npiUpdateSuccess && (
            <Box
              sx={{
                position: "absolute",
                right: -170,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 1,
              }}
            >
              <Typography
                sx={{
                  color: "green",
                  fontSize: "0.75rem",
                  whiteSpace: "nowrap",
                }}
              >
                Provider information updated!
              </Typography>
            </Box>
          )}
        </>
      )}
    </div>
  );
};

const AddressField = ({
  pk,
  keyName,
  editable,
  patientErrorMessage,
  handleAddressLineChange,
  editeValue,
  setHoveredFieldKey,
  highlightData,
  pinnedHighlights,
  togglePinnedHighlight,
  onFieldFocus,
  selectedFieldKey,
  ...fieldProps
}) => {
  const getAddressHighlightKey = (field, keyName) => {
    const mappings = {
      addressLine:
        keyName === "patientKeyValues"
          ? "Patient.Patient Address.Patient Street Line 1"
          : "Provider.Provider Address",
      city:
        keyName === "patientKeyValues"
          ? "Patient.Patient Address.Patient City"
          : "Provider.Provider City",
      state:
        keyName === "patientKeyValues"
          ? "Patient.Patient Address.Patient State"
          : "Provider.Provider State",
      zip:
        keyName === "patientKeyValues"
          ? "Patient.Patient Address.Patient Zip"
          : "Provider.Provider Zip",
    };
    return mappings[field];
  };

  const error = (field) =>
    Boolean(
      patientErrorMessage[keyName]?.[`${pk?.labelBindValue}`]?.[field]?.length
    );

  const helperText = (field) =>
    patientErrorMessage[keyName]?.[`${pk?.labelBindValue}`]?.[field];

  const commonProps = {
    fullWidth: true,
    size: "small",
    required: Boolean(pk?.labelStartColor),
    disabled: !editable,
  };

  const hasCoordinates = (field) => {
    const highlightKey = getAddressHighlightKey(field, keyName);
    return highlightKey && highlightData?.coordinates?.[highlightKey];
  };

  const handleFieldClick = (field) => {
    const highlightKey = getAddressHighlightKey(field, keyName);
    if (highlightKey && hasCoordinates(field)) {
      setHoveredFieldKey(highlightKey);
      if (onFieldFocus) {
        onFieldFocus(highlightKey);
      }
    } else {
      if (onFieldFocus) {
        onFieldFocus(null);
      }
    }
  };

  const handleIconClick = (field, e) => {
    e.stopPropagation();
    const highlightKey = getAddressHighlightKey(field, keyName);
    if (highlightKey && hasCoordinates(field)) {
      togglePinnedHighlight(highlightKey);
    }
  };

  const getBackgroundColor = (field) => {
    const highlightKey = getAddressHighlightKey(field, keyName);
      return hasCoordinates && selectedFieldKey === highlightKey
        ? "#FFFFBB"
        : editable
        ? bgColor
        : bgColorDisabled;

  };

  const getFieldStyles = (field) => {
    const highlightKey = getAddressHighlightKey(field, keyName);
    const hasCoordinates =
      highlightKey && highlightData?.coordinates?.[highlightKey];
    const isSelected = selectedFieldKey === highlightKey;

    const fieldBackgroundColor =
      isSelected && hasCoordinates
        ? "#FFFFBB"
        : editable
        ? bgColor
        : bgColorDisabled;

    return {
      "& .MuiFilledInput-root": {
        bgcolor: fieldBackgroundColor,

        "&.Mui-disabled": {
          bgcolor: fieldBackgroundColor,
        },
        "&:before": {
          borderBottomColor:
            hasCoordinates && selectedFieldKey === highlightKey
              ? "#FACD56"
              : hasCoordinates
              ? "#00AB4E"
              : "#202020",
        },
        "&.Mui-disabled:before": {
          borderBottomStyle: "dashed",
        },
        "&:hover:not(.Mui-disabled):before": {
          borderBottomColor:
            hasCoordinates && selectedFieldKey === highlightKey
              ? "#FACD56"
              : hasCoordinates
              ? "#00AB4E"
              : "#202020",
        },
        "&:after": {
          borderBottomColor: hasCoordinates ? "#FACD56" : "#202020",
        },
        "& .MuiInputBase-input.Mui-disabled": {
          color: hasCoordinates ? disabledOCRTextColor : disabledDefaultTextColor ,
          "-webkit-text-fill-color": hasCoordinates ? disabledOCRTextColor : disabledDefaultTextColor ,
        },

        "&.Mui-disabled .MuiInputBase-input": {
          color: hasCoordinates ? disabledOCRTextColor : disabledDefaultTextColor ,
          "-webkit-text-fill-color": hasCoordinates ? disabledOCRTextColor : disabledDefaultTextColor ,
        },
      },
      "& .MuiInputLabel-root.Mui-focused": {
        color: "rgba(0, 0, 0, 0.6)",
      },
      "& .MuiInputBase-root": {
        pointerEvents: hasCoordinates && !editable ? "none" : "auto",
      },
    };
  };

  return (
    <Stack sx={{ mt: 1, mb: 1 }}>
      {/* Address Line */}
      <div
        onClick={() => handleFieldClick("addressLine")}
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: "8px",
          cursor: hasCoordinates("addressLine") ? "pointer" : "default",
          marginBottom: "16px",
        }}
      >
        <MuiTextField
          {...commonProps}
          multiline
          sx={getFieldStyles("addressLine")}
          variant="filled"
          rows={1}
          label={pk?.labelName ?? "Patient Address"}
          name="addressLine"
          placeholder={editable ? "Address line 1" : undefined}
          value={pk?.labelValue?.addressLine || ""}
          onChange={
            editable ? (e) => handleAddressLineChange(e, keyName) : undefined
          }
          error={error("addressLine")}
          helperText={helperText("addressLine")}
          {...fieldProps}
        />
      </div>

      {/* City */}
      <div
        onClick={() => handleFieldClick("city")}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginBottom: "16px",
          cursor: hasCoordinates("city") ? "pointer" : "default",
        }}
      >
        <MuiTextField
          {...commonProps}
          label={editable ? pk?.labelAddressName?.city ?? "City" : "City"}
          name="city"
          sx={getFieldStyles("city")}
          variant="filled"
          placeholder={editable ? "City" : undefined}
          value={pk?.labelValue?.city || ""}
          onChange={editable ? (e) => editeValue(e, keyName) : undefined}
          error={error("city")}
          helperText={helperText("city")}
        />
      </div>

      {/* State and Zip in Grid */}
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <div
            onClick={() => handleFieldClick("state")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: hasCoordinates("state") ? "pointer" : "default",
            }}
          >
            <MuiTextField
              {...commonProps}
              label={
                editable ? pk?.labelAddressName?.state ?? "State" : "State"
              }
              name="state"
              sx={getFieldStyles("state")}
              variant="filled"
              placeholder={editable ? "State" : undefined}
              value={pk?.labelValue?.state || ""}
              onChange={editable ? (e) => editeValue(e, keyName) : undefined}
              error={error("state")}
              helperText={helperText("state")}
              inputProps={{ maxLength: 2 }}
            />
          </div>
        </Grid>
        <Grid item xs={6}>
          <div
            onClick={() => handleFieldClick("zip")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: hasCoordinates("zip") ? "pointer" : "default",
            }}
          >
            <MuiTextField
              {...commonProps}
              label={editable ? pk?.labelAddressName?.zip ?? "Zip" : "Zip"}
              name="zip"
              variant="filled"
              sx={getFieldStyles("zip")}
              placeholder={editable ? "Zip" : undefined}
              value={pk?.labelValue?.zip || ""}
              onChange={editable ? (e) => editeValue(e, keyName) : undefined}
              error={error("zip")}
              helperText={helperText("zip")}
              inputProps={{ maxLength: 5 }}
            />
          </div>
        </Grid>
      </Grid>
    </Stack>
  );
};


const FormFields = ({
  editable,
  processedData,
  patientErrorMessage,
  editeValue,
  handleAddressLineChange,
  orderTypeDropdown,
  setHoveredFieldKey,
  highlightData,
  pinnedHighlights,
  togglePinnedHighlight,
  faxId,
  childFaxId,
  reProcessFax,
  onFieldFocus,
  selectedFieldKey,
  ...props
}) => {
  const renderField = (pk, keyName) => {
    const fieldType = editable ? pk.labelEditType : pk.labelType;

    switch (fieldType) {
      case "address":
        return (
          <Box sx={{ width: "100%" }}>
            <AddressField
              pk={pk}
              keyName={keyName}
              editable={editable}
              patientErrorMessage={patientErrorMessage}
              handleAddressLineChange={handleAddressLineChange}
              editeValue={editeValue}
              setHoveredFieldKey={setHoveredFieldKey}
              selectedFieldKey={selectedFieldKey}
              highlightData={highlightData}
              pinnedHighlights={pinnedHighlights}
              togglePinnedHighlight={togglePinnedHighlight}
              onFieldFocus={onFieldFocus}
            />
          </Box>
        );

      case "select":
        if (!editable) return null;

        const highlightKey = "Patient.Patient Gender";

        const hasCoordinates =
          highlightKey && highlightData?.coordinates?.[highlightKey];

        const isGenderPinned = pinnedHighlights.has(highlightKey);

        const handleGenderClick = () => {
          if (highlightKey && hasCoordinates) {
            setHoveredFieldKey(highlightKey);

            if (onFieldFocus) {
              onFieldFocus(highlightKey);
            }
          }
        };

        const selectBackground =
          hasCoordinates && selectedFieldKey === highlightKey
            ? "#FFFFBB"
            : bgColor;

        return (
          <Box sx={{ width: "100%", maxWidth: "100%", mt: 0 }}>
            {pk?.labelGreyout ? (
              <CustomTextField
                pk={pk}
                keyName={keyName}
                editable={editable}
                patientErrorMessage={patientErrorMessage}
                editeValue={editeValue}
                highlightData={highlightData}
                setHoveredFieldKey={setHoveredFieldKey}
                selectedFieldKey={selectedFieldKey}
                pinnedHighlights={pinnedHighlights}
                togglePinnedHighlight={togglePinnedHighlight}
                reProcessFax={reProcessFax}
                faxId={faxId}
                childFaxId={childFaxId}
                onFieldFocus={onFieldFocus}
              />
            ) : (
              <div
                style={{
                  cursor: hasCoordinates ? "pointer" : "default",
                  width: "100%",
                }}
              >
                <FormControl
                  variant="filled"
                  fullWidth
                  size="small"
                  sx={{
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "rgba(0, 0, 0, 0.6)",
                    },
                  }}
                >
                  <InputLabel
                    id={`${pk.labelBindValue}-label`}
                    error={Boolean(
                      patientErrorMessage[keyName]?.[pk?.labelBindValue]?.length
                    )}
                  >
                    {pk?.labelName || "Gender"}
                  </InputLabel>
                  <Select
                    labelId={`${pk.labelBindValue}-label`}
                    size="small"
                    displayEmpty
                    onOpen={() => {
                      // Use onOpen instead of onClick which fires when dropdown opens
                      if (highlightKey && hasCoordinates) {
                        setHoveredFieldKey(highlightKey);
                        if (onFieldFocus) {
                          onFieldFocus(highlightKey);
                        }
                      }
                    }}
                    sx={{
                      "& .MuiFilledInput-root": {
                        bgcolor: selectBackground,

                        "&.Mui-disabled": {
                          bgcolor: selectBackground,
                        },
                        "&:before": {
                          borderBottomColor:
                            hasCoordinates && selectedFieldKey === highlightKey
                              ? "#FACD56"
                              : hasCoordinates
                              ? "#00AB4E"
                              : "#202020",
                        },
                        "&.Mui-disabled:before": {
                          borderBottomStyle: "dashed",
                        },
                        "&:hover:not(.Mui-disabled):before": {
                          borderBottomColor:
                            hasCoordinates && selectedFieldKey === highlightKey
                              ? "#FACD56"
                              : hasCoordinates
                              ? "#00AB4E"
                              : "#202020",
                        },
                        "&:after": {
                          borderBottomColor: hasCoordinates
                            ? "#FACD56"
                            : "#202020",
                        },
                        "& .MuiInputBase-input.Mui-disabled": {
                          color: hasCoordinates ? disabledOCRTextColor : disabledDefaultTextColor ,
                          "-webkit-text-fill-color": hasCoordinates ? disabledOCRTextColor : disabledDefaultTextColor ,
                        },

                        "&.Mui-disabled .MuiInputBase-input": {
                          color: hasCoordinates ? disabledOCRTextColor : disabledDefaultTextColor ,
                          "-webkit-text-fill-color": hasCoordinates ? disabledOCRTextColor : disabledDefaultTextColor ,
                        },
                      },
                      "& .MuiSelect-select": {

                        bgcolor: selectBackground,
                        "&:before": {
                          borderBottomColor:
                            hasCoordinates && selectedFieldKey === highlightKey
                              ? "#FACD56"
                              : hasCoordinates
                              ? "#00AB4E"
                              : "#202020",
                        },
                        "&.Mui-disabled:before": {
                          borderBottomStyle: "dashed",
                        },
                        "&:hover:not(.Mui-disabled):before": {
                          borderBottomColor:
                            hasCoordinates && selectedFieldKey === highlightKey
                              ? "#FACD56"
                              : hasCoordinates
                              ? "#00AB4E"
                              : "#202020",
                        },
                        "&:after": {
                          borderBottomColor: hasCoordinates
                            ? "#FACD56"
                            : "#202020",
                        },
                        "&.Mui-disabled .MuiSelect-select": {
                          color: hasCoordinates ? disabledOCRTextColor : disabledDefaultTextColor ,
                          "-webkit-text-fill-color": hasCoordinates ? disabledOCRTextColor : disabledDefaultTextColor ,
                        },
                      },
                      "&.MuiFilledInput-root": {
                        // Targets the root when Select is used directly
                        bgcolor: selectBackground,
                        "&:before": {
                          borderBottomColor:
                            hasCoordinates && selectedFieldKey === highlightKey
                              ? "#FACD56"
                              : hasCoordinates
                              ? "#00AB4E"
                              : "#202020",
                        },
                        "&.Mui-disabled:before": {
                          borderBottomStyle: "dashed",
                        },
                        "&:hover:not(.Mui-disabled):before": {
                          borderBottomColor:
                            hasCoordinates && selectedFieldKey === highlightKey
                              ? "#FACD56"
                              : hasCoordinates
                              ? "#00AB4E"
                              : "#202020",
                        },
                        "&:after": {
                          borderBottomColor: hasCoordinates
                            ? "#FACD56"
                            : "#202020",
                        },
                        "& .MuiInputBase-input.Mui-disabled": {
                          color: hasCoordinates ? disabledOCRTextColor : disabledDefaultTextColor ,
                          "-webkit-text-fill-color": hasCoordinates ? disabledOCRTextColor : disabledDefaultTextColor ,
                        },

                        "&.Mui-disabled .MuiInputBase-input": {
                          color: hasCoordinates ? disabledOCRTextColor : disabledDefaultTextColor ,
                          "-webkit-text-fill-color": hasCoordinates ? disabledOCRTextColor : disabledDefaultTextColor ,
                        },
                      },
                    }}
                    fullWidth
                    variant="filled"
                    value={pk?.labelValue || ""}
                    name={pk.labelBindValue}
                    onChange={(e) => editeValue(e, keyName)}
                    error={Boolean(
                      patientErrorMessage[keyName]?.[pk?.labelBindValue]?.length
                    )}
                  >
                    <MenuItem value="Gender" disabled>
                      Gender
                    </MenuItem>
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Female">Female</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>
              </div>
            )}
          </Box>
        );
      case "text":
      case "number":
      case "date":
        return (
          <CustomTextField
            pk={pk}
            keyName={keyName}
            editable={editable}
            patientErrorMessage={patientErrorMessage}
            editeValue={editeValue}
            type={fieldType}
            setHoveredFieldKey={setHoveredFieldKey}
            selectedFieldKey={selectedFieldKey}
            highlightData={highlightData}
            pinnedHighlights={pinnedHighlights}
            togglePinnedHighlight={togglePinnedHighlight}
            inputProps={{
              maxLength: fieldType === "number" ? 9 : undefined,
            }}
            reProcessFax={reProcessFax}
            faxId={faxId}
            childFaxId={childFaxId}
            onFieldFocus={onFieldFocus}
          />
        );

      default:
        return null;
    }
  };

  if (!processedData || Object.keys(processedData).length === 0) return null;

  return (
    <>
      {Object.keys(processedData).map((keyName, index) => (
        <Box key={keyName}>
          {(keyName === "patientKeyValues" ||
            (orderTypeDropdown !== "Existing" &&
              (keyName === "providerKeyValues" ||
                keyName === "orderKeyValues"))) && (
            <Stack
              direction={"row"}
              spacing={1}
              sx={{
                mb: 2,
                mt: keyName != "patientKeyValues" ? 2 : 0,
                alignItems: "center",
              }}
            >
              {" "}
              {keyName === "patientKeyValues" ? (
                <PersonOutlineIcon fontSize="32px" />
              ) : keyName === "providerKeyValues" ? (
                <PersonAddAltIcon fontSize="32px" />
              ) : (
                <DescriptionOutlinedIcon fontSize="32px" />
              )}
              <Typography
                sx={{
                  fontSize: "1rem",
                }}
              >
                {keyName === "patientKeyValues"
                  ? "Patient Information"
                  : keyName === "providerKeyValues"
                  ? "Provider Information"
                  : "Order Information"}
              </Typography>
            </Stack>
          )}

          <Grid container spacing={2}>
            {processedData[keyName]?.map((pk, fieldIndex) => {
              // Check if this is an order field that should be hidden
              const shouldHideOrderField =
                orderTypeDropdown === "Existing" &&
                keyName === "orderKeyValues";

              if (pk.labelHide === false) {
                if (
                  pk.labelType === "address" ||
                  pk.labelName === "Diagnosis Code"
                ) {
                  return (
                    <Grid
                      item
                      xs={12}
                      key={fieldIndex}
                      sx={{
                        visibility: shouldHideOrderField ? "hidden" : "visible",
                      }}
                    >
                      {renderField(pk, keyName)}
                    </Grid>
                  );
                }
                return (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    key={fieldIndex}
                    sx={{
                      visibility: shouldHideOrderField ? "hidden" : "visible",
                    }}
                  >
                    {renderField(pk, keyName)}
                  </Grid>
                );
              }
              return null;
            })}
          </Grid>
        </Box>
      ))}
    </>
  );
};

export default FormFields;
